import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Flow,
  FlowType,
  FollowupBand,
  Schedule,
  Rejection,
} from '@/services/api/clients/flowBuilder';
import flowBuilder from '@/services/api/clients/flowBuilder';

// SCRUM-1816: when the publish-blocked modal routes the operator to a
// broken follow-up, the question pane uses one of these discriminators to
// scroll past the parent Choices section onto the specific follow-up that
// failed. ``choiceLabel`` targets a per-choice follow-up; ``followupBand``
// targets a band-level (``*_response_followup``) follow-up.
export interface PendingRoleEditTarget {
  roleId: string;
  questionId?: string;
  choiceLabel?: string;
  followupBand?: FollowupBand;
}

interface FlowBuilderState {
  // Flow state
  activeFlow: FlowType;
  flowId: number | null;
  currentFlow: Flow | null;
  isLoadingFlow: boolean;
  flowError: string | null;

  // UI state
  selectedNodeId: string | null;
  // When set, RightPanel auto-opens the matching role's config pane and
  // RoleConfigPane scrolls to / highlights the matching question. Used to
  // jump from the publish-blocked modal directly to a broken question.
  pendingRoleEditTarget: PendingRoleEditTarget | null;
  selectedContext: string; // "location-{id}" or "landing-{uuid}"
  isDuplicateModalOpen: boolean;
  isTestFlowOpen: boolean;
  isSaveModalOpen: boolean;
  isPublishModalOpen: boolean;
  shouldAutoOpenCalendarSelector: boolean;

  // Auto-save state
  saveStatus: 'idle' | 'pending' | 'saved' | 'error';

  // Monotonic counter bumped on every optimistic edit and on context/flow
  // switches. A post-save reload captures it beforehand and discards its
  // result if the value has advanced — i.e. the user changed something while
  // the reload was in flight — so a stale server snapshot can't clobber a
  // newer local edit.
  editGeneration: number;

  // Refresh mechanism
  refreshKey: number;

  // Actions
  setActiveFlow: (flow: FlowType) => void;
  setFlowId: (id: number | null) => void;
  setCurrentFlow: (flow: Flow | null) => void;
  setIsLoadingFlow: (loading: boolean) => void;
  setFlowError: (error: string | null) => void;
  setSelectedNodeId: (nodeId: string | null) => void;
  setPendingRoleEditTarget: (target: PendingRoleEditTarget | null) => void;
  setSelectedContext: (context: string) => void;
  setIsDuplicateModalOpen: (open: boolean) => void;
  setIsTestFlowOpen: (open: boolean) => void;
  setIsSaveModalOpen: (open: boolean) => void;
  setIsPublishModalOpen: (open: boolean) => void;
  setShouldAutoOpenCalendarSelector: (open: boolean) => void;
  setSaveStatus: (status: 'idle' | 'pending' | 'saved' | 'error') => void;
  triggerRefresh: () => void;

  // Async actions
  loadFlow: (
    contextId: string,
    flowType: FlowType,
    options?: { guardGeneration?: number }
  ) => Promise<void>;
  createFlow: (contextId: string, flowType: FlowType) => Promise<void>;
  toggleFlowState: (isActive: boolean) => Promise<void>;
  updateFlowSchedule: (updates: Partial<Schedule>) => void;
  updateFlowRejection: (updates: Partial<Rejection>) => void;
  reset: () => void;
}

const initialState = {
  activeFlow: 'hiring' as FlowType,
  flowId: null,
  currentFlow: null,
  isLoadingFlow: false,
  flowError: null,
  selectedNodeId: null,
  pendingRoleEditTarget: null,
  selectedContext: '',
  isDuplicateModalOpen: false,
  isTestFlowOpen: false,
  isSaveModalOpen: false,
  isPublishModalOpen: false,
  shouldAutoOpenCalendarSelector: false,
  saveStatus: 'idle' as const,
  editGeneration: 0,
  refreshKey: 0,
};

export const useFlowBuilderStore = create<FlowBuilderState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Setters
      setActiveFlow: (flow) =>
        set((state) => ({ activeFlow: flow, editGeneration: state.editGeneration + 1 })),
      setFlowId: (id) => set({ flowId: id }),
      setCurrentFlow: (flow) => set({ currentFlow: flow }),
      setIsLoadingFlow: (loading) => set({ isLoadingFlow: loading }),
      setFlowError: (error) => set({ flowError: error }),
      setSelectedNodeId: (nodeId) => set({ selectedNodeId: nodeId }),
      setPendingRoleEditTarget: (target) => set({ pendingRoleEditTarget: target }),
      setSelectedContext: (context) => {
        const { selectedContext, editGeneration } = get();
        if (selectedContext !== context) {
          set({
            selectedContext: context,
            currentFlow: null,
            flowId: null,
            isLoadingFlow: !!context,
            // Invalidate any reload still in flight for the previous context.
            editGeneration: editGeneration + 1,
          });
        }
      },
      setIsDuplicateModalOpen: (open) => set({ isDuplicateModalOpen: open }),
      setIsTestFlowOpen: (open) => set({ isTestFlowOpen: open }),
      setIsSaveModalOpen: (open) => set({ isSaveModalOpen: open }),
      setIsPublishModalOpen: (open) => set({ isPublishModalOpen: open }),
      setShouldAutoOpenCalendarSelector: (open) => set({ shouldAutoOpenCalendarSelector: open }),
      setSaveStatus: (status) => set({ saveStatus: status }),
      triggerRefresh: () => set((state) => ({ refreshKey: state.refreshKey + 1 })),

      // Async actions
      loadFlow: async (contextId: string, flowType: FlowType, options) => {
        // Context switch handling is now done in setSelectedContext setter.
        const { guardGeneration } = options ?? {};
        const isReconcile = guardGeneration !== undefined;
        // A reconcile reload runs after auto-save purely to refresh server-owned
        // data. It is "stale" if the user has edited (or switched flow/context)
        // since it was kicked off — applying it would clobber that newer state.
        const isStaleReconcile = () => isReconcile && get().editGeneration !== guardGeneration;

        // Keep reconcile reloads silent so they don't flip the global loading
        // state and disrupt the pane the user is actively editing.
        if (!isReconcile) {
          set({ isLoadingFlow: true, flowError: null });
        }

        try {
          if (typeof contextId !== 'string') {
            // Guard against invalid types (e.g. from legacy state or incorrect calls)
            console.error('Invalid contextId passed to loadFlow:', contextId);
            set({ isLoadingFlow: false, flowError: 'Invalid context ID' });
            return;
          }

          let checkResult;
          let flows: Flow[] = [];

          if (contextId.startsWith('location-')) {
            const locationId = contextId.replace('location-', '');
            if (!locationId) throw new Error('Invalid location ID');

            checkResult = await flowBuilder.checkFlow(locationId, flowType);
            if (checkResult.exists && checkResult.flow_id) {
              flows = await flowBuilder.getFlows(locationId);
            }
          } else if (contextId.startsWith('landing-')) {
            const orgIdStr = contextId.replace('landing-', '');
            // Backend handles both integer IDs and UUID strings for organizations
            checkResult = await flowBuilder.checkLandingFlow(orgIdStr, flowType);
            if (checkResult.exists && checkResult.flow_id) {
              flows = await flowBuilder.getLandingFlows(orgIdStr);
            }
          } else {
            // Fallback for bare UUID location IDs
            const locationId = contextId;
            if (locationId) {
              checkResult = await flowBuilder.checkFlow(locationId, flowType);
              if (checkResult.exists && checkResult.flow_id) {
                flows = await flowBuilder.getFlows(locationId);
              }
            } else {
              throw new Error('Invalid context ID format');
            }
          }

          // Drop the result if a newer edit/switch happened while we fetched.
          if (isStaleReconcile()) return;

          if (checkResult && checkResult.exists && checkResult.flow_id) {
            const flow = flows.find((f) => f.type === flowType);

            if (flow) {
              set({
                currentFlow: flow,
                flowId: flow.id,
                flowError: null,
              });
            } else {
              set({ flowError: 'Flow found in check but not in list' });
            }
          } else {
            set({
              currentFlow: null,
              flowId: null,
              flowError: null,
            });
          }
        } catch (error) {
          console.error('Failed to load flow:', error);
          // Never surface a stale reconcile's error/reset over newer local state.
          if (isStaleReconcile()) return;
          if (error instanceof Error && error.message.includes('not found')) {
            set({
              currentFlow: null,
              flowId: null,
              flowError: null,
            });
          } else {
            set({
              flowError: error instanceof Error ? error.message : 'Failed to load flow',
            });
          }
        } finally {
          if (!isReconcile) {
            set({ isLoadingFlow: false });
          }
        }
      },

      createFlow: async (contextId: string, flowType: FlowType) => {
        set({ isLoadingFlow: true, flowError: null });

        try {
          let flow;
          if (contextId.startsWith('location-')) {
            const locationId = contextId.replace('location-', '');
            if (!locationId) throw new Error('Invalid location ID');
            flow = await flowBuilder.createFlow(locationId, flowType);
          } else if (contextId.startsWith('landing-')) {
            const orgIdStr = contextId.replace('landing-', '');
            // Backend handles both integer IDs and UUID strings for organizations
            flow = await flowBuilder.createLandingFlow(orgIdStr, flowType);
          } else {
            // Fallback for bare UUID location IDs
            const locationId = contextId;
            if (locationId) {
              flow = await flowBuilder.createFlow(locationId, flowType);
            } else {
              throw new Error('Invalid context ID format for creation');
            }
          }

          set({
            currentFlow: flow,
            flowId: flow.id,
            flowError: null,
          });
        } catch (error) {
          console.error('Failed to create flow:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to create flow';

          set({
            flowError: errorMessage,
          });
        } finally {
          set({ isLoadingFlow: false });
        }
      },

      toggleFlowState: async (isActive: boolean) => {
        const { flowId, currentFlow } = get();
        if (!flowId || !currentFlow) return;

        // Optimistic update
        set({ currentFlow: { ...currentFlow, is_active: isActive } });

        try {
          const updatedFlow = await flowBuilder.toggleFlowState(flowId, isActive);
          set({ currentFlow: updatedFlow });
        } catch (error) {
          // Revert on error
          set({ currentFlow });
          console.error('Failed to toggle flow state:', error);
        }
      },

      updateFlowSchedule: (updates) =>
        set((state) => {
          if (!state.currentFlow || !state.currentFlow.schedule) return {};
          return {
            editGeneration: state.editGeneration + 1,
            currentFlow: {
              ...state.currentFlow,
              schedule: {
                ...state.currentFlow.schedule,
                ...updates,
              },
            },
          };
        }),

      updateFlowRejection: (updates) =>
        set((state) => {
          if (!state.currentFlow || !state.currentFlow.rejection) return {};
          return {
            editGeneration: state.editGeneration + 1,
            currentFlow: {
              ...state.currentFlow,
              rejection: {
                ...state.currentFlow.rejection,
                ...updates,
              },
            },
          };
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'flow-builder-storage',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist key state that should survive page navigation
      partialize: (state) => ({
        activeFlow: state.activeFlow,
        flowId: state.flowId,
        currentFlow: state.currentFlow,
        selectedNodeId: state.selectedNodeId,
        selectedContext: state.selectedContext,
      }),
    }
  )
);
